import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle, faArrowRight, faCreditCard, faSpinner } from '@/fontawesome';
import { analytics, AnalyticsEvents } from '@/services/analytics';
import { useUser } from '@/contexts/UserContext';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';
import { getPlanShortName } from '@/consts/pricingPublic';
import { abortChainStep, clearChain, continueChain, getAllOwnedMessage, type ChainProgress } from '@/services/checkoutChain';
import { showErrorToast, TOAST_MESSAGES } from '@/utils/toasts';
import { getUserFacingError } from '@/utils/errorMessages';

/**
 * Landing nach einem abgebrochenen Stripe-Checkout (`/checkout/cancel?subscriptionId=…`).
 *
 * Gehört die Subscription zu einer Checkout-Kette (mehrere Pakete aus einem Link), wird der
 * abgebrochene Schritt wieder geöffnet und kann direkt erneut gestartet werden. War davor
 * bereits ein Paket gebucht, sagt die Seite das ausdrücklich – „nichts berechnet“ wäre falsch.
 */
export default function CheckoutCancelPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subscriptionId = searchParams.get('subscriptionId');
  const { user } = useUser();
  const { products } = useSubscriptionProducts();

  // Abgebrochenen Ketten-Schritt einmalig beim Mount zurücksetzen (idempotent)
  const [progress] = useState<ChainProgress | null>(() => abortChainStep(subscriptionId));
  const [continuing, setContinuing] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);
  const showPartialSuccess = progress !== null && progress.hasCompletedStep;

  useEffect(() => {
    analytics.track(AnalyticsEvents.CHECKOUT_CANCELLED, progress !== null
      ? {
        productType: progress.chain.steps[progress.stepIndex].plan,
        chainStep: progress.stepIndex + 1,
        chainTotal: progress.total,
      }
      : {});
  }, [progress]);

  // Rückkehr aus dem Back/Forward-Cache (Browser-Zurück von Stripe): Spinner des vorherigen Klicks lösen
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setContinuing(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Warnung beim Schließen, solange ein Teil der Kette bezahlt ist. Während `continuing` aus,
  // weil auch der Redirect zu Stripe (`window.location.href`) `beforeunload` auslöst.
  useEffect(() => {
    if (!showPartialSuccess || continuing) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => { window.removeEventListener('beforeunload', handleBeforeUnload); };
  }, [showPartialSuccess, continuing]);

  const handleRetry = async () => {
    if (progress === null || user === null || continuing) return;
    setContinuing(true);
    const openCount = progress.chain.steps.filter(step => step.status === 'pending' || step.status === 'started').length;
    try {
      const result = await continueChain(user, progress.chain, products);
      if (result === 'DONE') {
        // Nichts mehr zu buchen (z.B. inzwischen in einem anderen Tab gekauft)
        showErrorToast(getAllOwnedMessage(openCount));
        navigate('/dashboard', { replace: true });
      }
      // REDIRECTING -> Browser geht zu Stripe, Spinner absichtlich stehen lassen
    } catch (error) {
      console.error('Restarting checkout chain step failed:', error);
      setContinuing(false);
      setRetryFailed(true);
      showErrorToast(getUserFacingError(error, TOAST_MESSAGES.GENERAL_ERROR));
    }
  };

  const handleLater = () => {
    clearChain();
    navigate('/dashboard', { replace: true });
  };

  const cancelledPlanName = progress !== null
    ? getPlanShortName(products, progress.chain.steps[progress.stepIndex].plan)
    : '';
  const completedPlanNames = progress !== null
    ? progress.chain.steps.filter(step => step.status === 'done').map(step => getPlanShortName(products, step.plan))
    : [];

  const retryButton = (
    <button
      type="button"
      onClick={() => { void handleRetry(); }}
      disabled={continuing || user === null}
      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-wait cursor-pointer transition-colors font-medium"
    >
      {continuing ? (
        <>
          <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
          Weiterleitung zum Checkout…
        </>
      ) : (
        <>
          <FontAwesomeIcon icon={faCreditCard} className="w-4 h-4" />
          {showPartialSuccess ? `${cancelledPlanName} jetzt buchen` : 'Checkout erneut starten'}
        </>
      )}
    </button>
  );

  const secondaryButtonClass = 'inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 cursor-pointer transition-colors font-medium dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 disabled:cursor-not-allowed';

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-lg w-full text-center">
        <div className="mb-6">
          <div className="w-48 h-48 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto">
            <FontAwesomeIcon icon={faTimesCircle} className="text-amber-500 text-7xl sm:text-8xl" />
          </div>
        </div>

        {showPartialSuccess ? (
          <>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Fast geschafft
            </h1>

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {completedPlanNames.join(' und ')} {completedPlanNames.length > 1 ? 'wurden' : 'wurde'} bereits gebucht.
              <br />
              Den Checkout für {cancelledPlanName} hast du abgebrochen – dieses Paket ist noch nicht gebucht.
            </p>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6 text-left">
              <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">Wichtig vorab:</p>
              <ul className="text-gray-600 dark:text-gray-400 space-y-1 text-sm">
                <li>• Dein bereits gebuchtes Paket wird unabhängig davon aktiviert.</li>
                <li>• Schließe jetzt die Buchung von {cancelledPlanName} ab, um alles freizuschalten.</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {retryButton}
              {retryFailed && (
                <button
                  type="button"
                  onClick={handleLater}
                  disabled={continuing}
                  className={secondaryButtonClass}
                >
                  Später
                  <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Abo-Aktivierung abgebrochen
            </h1>

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Du hast die Abo-Aktivierung im Checkout abgebrochen.
              <br />
              Es wurde noch nichts aktiviert oder berechnet.
            </p>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6 text-left">
              <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">Wichtig vorab:</p>
              <ul className="text-gray-600 dark:text-gray-400 space-y-1 text-sm">
                <li>• Du kannst jederzeit zurückkehren und das Abo aktivieren.</li>
                <li>• Kein Zeitdruck – du entscheidest, wann du startest.</li>
              </ul>
            </div>

            <p className="text-gray-700 dark:text-gray-300 font-medium mb-6">
              Entscheide selbst, wann du an der Börse durchstarten möchtest.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {progress !== null ? (
                <>
                  {/* Ketten-Pakete (z.B. Community, Ausbildung) sind auf /pricing nicht gelistet –
                      der Neustart muss denselben Schritt erneut anstoßen. */}
                  {retryButton}
                  <button
                    type="button"
                    onClick={handleLater}
                    disabled={continuing}
                    className={secondaryButtonClass}
                  >
                    Zum Dashboard
                    <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors font-medium"
                  >
                    <FontAwesomeIcon icon={faCreditCard} className="w-4 h-4" />
                    Checkout erneut starten
                  </Link>
                  <Link to="/dashboard" className={secondaryButtonClass}>
                    Zum Dashboard
                    <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
                  </Link>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
